import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { ArrowLeft, Send } from "lucide-react"
import { useEffect, useRef } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import { t } from "@/lib/i18n"
import type { ConversationsShow } from "@/types/serializers"
import type RequestsCreateMessage from "@/types/serializers/RequestsCreateMessage"

function formatTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function ConversationShow({
  conversation,
  other_participant,
  messages,
  is_blocked,
}: ConversationsShow) {
  const { auth } = usePage<{ auth: { user: { id: number } } }>().props
  const currentUserId = auth.user.id
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const roleLabel =
    t.roles[other_participant.role as keyof typeof t.roles] ??
    other_participant.role

  const { data, setData, post, processing } = useForm<RequestsCreateMessage>({
    message: { body: "" },
  })

  useEffect(() => {
    if (
      messagesEndRef.current &&
      typeof messagesEndRef.current.scrollIntoView === "function"
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages.length])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!data.message.body.trim()) return

    post(`/mensajes/${conversation.id}/messages`, {
      onSuccess: () => setData("message", { body: "" }),
      preserveScroll: true,
    })
  }

  return (
    <AppLayout>
      <Head title={`${other_participant.full_name} | Mensajes | KrownFT`} />

      <div className="bg-background flex min-h-svh flex-col">
        {/* Header */}
        <div className="border-border bg-card sticky top-0 z-10 border-b px-4 py-3">
          <div className="mx-auto flex max-w-2xl items-center gap-3">
            <Link
              href="/mensajes"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Avatar className="h-10 w-10">
              <AvatarImage
                src={other_participant.avatar_url ?? undefined}
                alt={other_participant.full_name}
              />
              <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
                {other_participant.first_name[0]}
                {other_participant.last_name[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <span className="font-semibold">
                {other_participant.full_name}
              </span>
              <Badge variant="outline" className="ml-2 text-xs">
                {roleLabel}
              </Badge>
              <VerifiedBadge
                show={other_participant.profile_verified}
                size="sm"
              />
            </div>
            <ContentActionMenu
              reportableType="User"
              reportableId={other_participant.id}
              hideableType="User"
              hideableId={other_participant.id}
              showBlock
              blockUserId={other_participant.id}
              isBlocked={is_blocked}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="mx-auto max-w-2xl space-y-3">
            {messages.length > 0 ? (
              messages.map((msg) => {
                const isMine = msg.sender.id === currentUserId

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        isMine
                          ? "bg-krown-200 text-white"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      <p className="text-sm">{msg.body}</p>
                      <p
                        className={`mt-0.5 text-xs ${isMine ? "text-white/60" : "text-muted-foreground"}`}
                      >
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="py-16 text-center">
                <p className="text-muted-foreground">
                  Iniciá la conversación con {other_participant.first_name}
                </p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input bar */}
        <div className="border-border bg-card sticky bottom-0 border-t px-4 py-3">
          <form
            onSubmit={handleSubmit}
            className="mx-auto flex max-w-2xl gap-2"
          >
            <input
              type="text"
              value={data.message.body}
              onChange={(e) =>
                setData("message", { ...data.message, body: e.target.value })
              }
              placeholder="Escribí un mensaje..."
              className="focus:border-krown-300 border-border bg-secondary flex-1 rounded-full border px-4 py-2 text-sm focus:outline-none"
            />
            <Button
              type="submit"
              size="sm"
              disabled={processing || !data.message.body.trim()}
              className="bg-krown-200 hover:bg-krown-100 rounded-full text-white"
              aria-label="Enviar"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
